import { seesRentalsOfEveryEvent, type Me } from '../auth/me';
import { groupItems, type ItemGroup } from '../catalog/grouping';
import type { Category } from '../catalog/ItemForm';
import { formatPln } from '../catalog/money';
import type { PriceView } from './types';

/** The name a position is shown with: our item's, or the supplier's own. */
export const positionName = (p: PriceView): string => p.itemName ?? p.name ?? '';

/** Lower case, no Polish diacritics ("ł" as "l"), single spaces: what search compares. */
const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/ł/g, 'l')
    .replace(/\s+/g, ' ')
    .trim();

/** Every word of `q` in the position's name, description or notes (case and diacritics ignored); empty `q`: all. */
export function filterPositions(list: PriceView[], q: string): PriceView[] {
  const words = fold(q).split(' ').filter(Boolean);
  if (words.length === 0) return list;
  return list.filter((p) => {
    const text = fold(`${positionName(p)} ${p.name ?? ''} ${p.description ?? ''} ${p.notes ?? ''}`);
    return words.every((w) => text.includes(w));
  });
}

/**
 * The supplier's catalog grouped like ours (owner 2026-10-21 00:08: "taki sam katalog jak mój"): positions under OUR
 * categories — our items' by the item, the supplier's own by the category chosen for them (owner 2026-10-23 20:22);
 * the supplier's own positions without a category at the end, as "Spoza naszego katalogu".
 */
export function supplierGroups(list: PriceView[], categories: Category[]): ItemGroup<PriceView>[] {
  const known = new Set(categories.map((c) => c.id));
  const loose = (p: PriceView) => !p.itemId && !(p.categoryId && known.has(p.categoryId));
  const ours = list.filter((p) => !loose(p));
  const theirs = list.filter(loose);
  const groups = groupItems(ours, categories);
  // a single unlabelled group (no categories known): give it a name when the supplier's own positions follow
  if (groups.length === 1 && groups[0]!.label === null && theirs.length > 0) groups[0] = { ...groups[0]!, name: 'Nasz katalog', label: 'Nasz katalog' };
  if (theirs.length > 0) groups.push({ id: null, name: 'Spoza naszego katalogu', label: 'Spoza naszego katalogu', depth: 0, items: theirs });
  return groups;
}

/** The statistics of a position (`GET /api/subrentals/suppliers/:id/stats`); the cost only for whoever sees it. */
export type PositionStats = { priceId: string; rentals: number; costGrosze?: number };

/**
 * The details' lines (owner 2026-10-22 00:07): "Dopożyczane: N razy" and — `withCost` or a cost in `stats` —
 * "Łączny koszt: X zł". A position absent from the statistics was never rented (0).
 */
export function positionStatsLines(stats: { rentals: number; costGrosze?: number } | undefined, withCost = false): string[] {
  const n = stats?.rentals ?? 0;
  const lines = [`Dopożyczane: ${n} ${n === 1 ? 'raz' : 'razy'}`];
  const cost = stats ? stats.costGrosze : withCost ? 0 : undefined;
  if (cost !== undefined) lines.push(`Łączny koszt: ${formatPln(cost)}`);
  return lines;
}

/**
 * The statistics of a supplier's positions (owner 2026-10-22 00:07) cover every event: the owner, or the rentals in
 * scope „wszystkie” with „Domyślnie widzi wszystkie wydarzenia” (review M2, „Kto widzi” I7 — else the server says 403).
 */
export const seesSupplierStats = (me: Me): boolean => me.isOwner || seesRentalsOfEveryEvent(me);
